import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '@/lib/api.js';
import { useParams } from 'react-router-dom';

// URL Generator — stitches event.primary_ticketing_url to ref_codes registry.
// Pick an event → get one URL per active ref code, plus a custom-code field.

function formatDateShort(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });
}

function appendRef(url, code) {
  if (!url) return '';
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}ref=${encodeURIComponent(code)}`;
}

export default function UrlGenerator() {
  const { tenantSlug } = useParams();
  const [events, setEvents] = useState([]);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPast, setShowPast] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [custom, setCustom] = useState('');
  const [copiedKey, setCopiedKey] = useState(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      apiFetch(`/api/tenants/${tenantSlug}/events?all=1`).then((r) => r.json()),
      apiFetch(`/api/tenants/${tenantSlug}/ref-codes`).then((r) => r.json()),
    ]).then(([e, r]) => {
      if (e.error || r.error) throw new Error(e.error || r.error);
      setEvents(e.events || []);
      setCodes((r.ref_codes || []).filter((c) => c.active));
    }).catch((err) => setError(err.message)).finally(() => setLoading(false));
  }, [tenantSlug]);

  const todayUtc = useMemo(() => {
    const d = new Date();
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())).toISOString();
  }, []);

  const visibleEvents = useMemo(() => {
    const filtered = showPast ? events : events.filter((e) => e.date >= todayUtc);
    return filtered.slice().sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  }, [events, showPast, todayUtc]);

  const selected = useMemo(
    () => visibleEvents.find((e) => e.id === selectedId) || null,
    [visibleEvents, selectedId],
  );

  const primaryUrl = useMemo(() => {
    if (!selected) return null;
    const platform = selected.primary_ticketing_platform || 'universe';
    const pub = (selected.publications || []).find((p) => p.platform === platform);
    return pub?.external_url || null;
  }, [selected]);

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1500);
    } catch (_e) {
      // Clipboard may fail in non-secure contexts; fall back to prompt.
      window.prompt('Copy this URL:', text);
    }
  };

  const copyAll = () => {
    if (!primaryUrl) return;
    const lines = codes.map((c) => `${c.code}\t${appendRef(primaryUrl, c.code)}`);
    copy(lines.join('\n'), '__all__');
  };

  if (loading) return <div className="text-muted-foreground">Loading…</div>;
  if (error) return <div className="text-destructive">Error: {error}</div>;

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">URL generator</h1>
        <div className="text-sm text-muted-foreground">
          {codes.length} active ref codes
        </div>
      </div>

      <section className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex-1">Event</label>
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={showPast}
              onChange={(e) => setShowPast(e.target.checked)}
              className="rounded"
            />
            Include past
          </label>
        </div>
        <select
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
          className="w-full px-3 py-2 border border-input rounded-md bg-background text-sm"
        >
          <option value="">· Pick an event ({visibleEvents.length} available) —</option>
          {visibleEvents.map((e) => (
            <option key={e.id} value={e.id}>
              {formatDateShort(e.date)} · {e.talent}
              {e.is_placeholder ? ' (placeholder)' : ''}
            </option>
          ))}
        </select>
      </section>

      {selected && !primaryUrl && (
        <div className="border border-amber-500/40 bg-amber-500/5 rounded-md p-4 text-sm">
          Event not yet published. Generate URLs after the Publish step.
          <div className="text-xs text-muted-foreground mt-1">
            This event has no <span className="font-mono">universe</span> publication with an external URL.
          </div>
        </div>
      )}

      {selected && primaryUrl && (
        <>
          <section className="mb-6">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Primary ticketing URL</div>
            <div className="border border-border rounded-md p-3 bg-card flex items-center gap-2">
              <a
                href={primaryUrl}
                target="_blank"
                rel="noreferrer"
                className="font-mono text-xs text-primary hover:underline flex-1 truncate"
              >{primaryUrl}</a>
              <button
                onClick={() => copy(primaryUrl, '__base__')}
                className="px-2 py-1 text-xs border border-input rounded-md hover:bg-accent flex-shrink-0"
              >{copiedKey === '__base__' ? '✓ Copied' : 'Copy base'}</button>
            </div>
          </section>

          <section className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Ref codes ({codes.length} active)
              </div>
              <button
                onClick={copyAll}
                className="px-2 py-1 text-xs border border-input rounded-md hover:bg-accent"
              >{copiedKey === '__all__' ? '✓ Copied all' : 'Copy all'}</button>
            </div>
            <div className="border border-border rounded-md overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left">
                  <tr>
                    <th className="px-3 py-2 font-medium w-40">Code</th>
                    <th className="px-3 py-2 font-medium">URL</th>
                    <th className="px-3 py-2 font-medium w-20 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {codes.length === 0 && (
                    <tr><td colSpan={3} className="px-3 py-6 text-center text-muted-foreground">No active ref codes for this tenant.</td></tr>
                  )}
                  {codes.map((c) => {
                    const url = appendRef(primaryUrl, c.code);
                    return (
                      <tr key={c.id} className="border-t border-border hover:bg-accent/30">
                        <td className="px-3 py-2 font-mono text-xs">{c.code}</td>
                        <td className="px-3 py-2 font-mono text-xs text-muted-foreground truncate max-w-xl">{url}</td>
                        <td className="px-3 py-2 text-right">
                          <button
                            onClick={() => copy(url, c.id)}
                            className="px-2 py-0.5 text-xs border border-input rounded-md hover:bg-accent"
                          >{copiedKey === c.id ? '✓' : 'Copy'}</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
              Custom code (not in registry)
            </div>
            <div className="border border-border rounded-md p-3 bg-card">
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="text"
                  value={custom}
                  onChange={(e) => setCustom(e.target.value.trim())}
                  placeholder="e.g. influencer123"
                  className="flex-1 px-2 py-1 border border-input rounded-md bg-background text-sm font-mono"
                />
                <button
                  onClick={() => custom && copy(appendRef(primaryUrl, custom), '__custom__')}
                  disabled={!custom}
                  className="px-2 py-1 text-xs border border-input rounded-md hover:bg-accent disabled:opacity-50"
                >{copiedKey === '__custom__' ? '✓ Copied' : 'Copy'}</button>
              </div>
              {custom && (
                <div className="font-mono text-xs text-muted-foreground truncate">
                  {appendRef(primaryUrl, custom)}
                </div>
              )}
              <div className="text-[11px] text-muted-foreground mt-2">
                One-off codes (influencer giveaways, etc.). Not written to the ref-codes registry.
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
